import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { AccessDeniedState } from '@/components/shared/access-denied-state'
import {
  IntegrationsSidebar,
  defaultIntegrationsSection,
  type IntegrationsSection as IntegrationsSectionValue,
} from '@/components/settings/integrations-sidebar'
import { IntegrationsSection as ConnectedAppsSection } from '@/components/settings/organization/integrations-section'
import { CalendarSection } from '@/components/settings/email/calendar-section'
import { ApiKeysSection } from '@/components/settings/organization/api-keys-section'
import { WebhookEndpointsSection } from '@/components/settings/integrations/webhook-endpoints-section'
import { usePermissions } from '@/hooks/use-permissions'
import { requirePermission, PermissionDeniedRoute } from '@/lib/require-permission-guard'
import { useCurrentOrganization } from '@/stores/organization.store'

type IntegrationsSearch = {
  section?: IntegrationsSectionValue
}

export const Route = createFileRoute('/_app/_settings/integrations')({
  component: IntegrationsPage,
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => {
    return {
      section: (search.section as IntegrationsSectionValue) || 'apps',
    }
  },
  beforeLoad: async ({ context }) => {
    await requirePermission(
      context.queryClient,
      ['SettingsManage', 'AutomationsView'],
      'integration settings'
    )
  },
  errorComponent: PermissionDeniedRoute,
})

function IntegrationsPage() {
  useBreadcrumbFromRoute()
  const search = useSearch({ from: '/_app/_settings/integrations' })
  const { currentOrganization } = useCurrentOrganization()
  const { can } = usePermissions(currentOrganization?.id ?? '')
  const canManageSettings = can('SettingsManage')
  const canViewAutomations = can('AutomationsView')
  const currentSection =
    search.section || defaultIntegrationsSection(canManageSettings, canViewAutomations)
  const canViewSection = currentSection === 'webhooks' ? canViewAutomations : canManageSettings

  return (
    <div className="flex h-full overflow-hidden">
      <div className="scroll-fade-y flex-shrink-0 overflow-y-auto">
        <IntegrationsSidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          {!canViewSection ? (
            <AccessDeniedState
              variant="section"
              noun={currentSection === 'webhooks' ? 'webhooks' : 'integration settings'}
            />
          ) : (
            <>
              {currentSection === 'apps' && <ConnectedAppsSection />}
              {currentSection === 'calendar' && <CalendarSection />}
              {currentSection === 'api-keys' && <ApiKeysSection />}
              {currentSection === 'webhooks' && <WebhookEndpointsSection />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
